import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'md' | 'lg';

interface SharedProps {
  readonly variant?: Variant;
  readonly size?: Size;
  readonly children: ReactNode;
  readonly className?: string;
}

type ButtonAsButton = SharedProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    readonly href?: undefined;
  };

type ButtonAsInternalLink = SharedProps &
  Omit<LinkProps, 'to' | 'className'> & {
    readonly href: string;
  };

type ButtonAsExternalLink = SharedProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'className'> & {
    readonly href: string;
    readonly external: true;
  };

export type ButtonProps = ButtonAsButton | ButtonAsInternalLink | ButtonAsExternalLink;

function isExternal(href: string): boolean {
  return /^([a-z]+:|\/\/)/i.test(href) && !href.startsWith('/');
}

/**
 * The one button component for the whole site. Renders as a real <button>
 * when there's no href, an internal <Link> for in-app routes, and a plain
 * <a rel="noopener noreferrer" target="_blank"> for external/`mailto:`/`tel:` links.
 */
export function Button(props: ButtonProps) {
  const { variant = 'primary', size = 'md', children, className, ...rest } = props;
  const classes = [styles.button, styles[variant], styles[size], className]
    .filter(Boolean)
    .join(' ');

  if ('href' in rest && typeof rest.href === 'string') {
    const { href, ...anchorRest } = rest;
    const treatAsExternal = 'external' in rest || isExternal(href);

    if (treatAsExternal) {
      const { external: _external, ...anchorProps } = anchorRest as Omit<
        ButtonAsExternalLink,
        'href'
      >;
      const isProtocolLink = href.startsWith('mailto:') || href.startsWith('tel:');
      return (
        <a
          href={href}
          className={classes}
          target={isProtocolLink ? undefined : '_blank'}
          rel={isProtocolLink ? undefined : 'noopener noreferrer'}
          {...anchorProps}
        >
          {children}
        </a>
      );
    }

    return (
      <Link to={href} className={classes} {...(anchorRest as Omit<LinkProps, 'to' | 'className'>)}>
        {children}
      </Link>
    );
  }

  return (
    <button className={classes} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>
      {children}
    </button>
  );
}
